import { useState, useCallback } from 'react';
import { ncfAPI } from '../../services/api';
import Typeahead from '../Typeahead';

const ACCEPT = '.pdf,.docx,.png,.jpg,.jpeg';
const MAX_FILE_BYTES = 15 * 1024 * 1024;

const initialForm = {
  candidateFirstName: '',
  candidateLastName: '',
  candidateEmail: '',
  payRate: '',
  payRateType: 'hourly',
  desiredStartDate: '',
  clientName: '',
  notes: ''
};

function NcfUploadModal({ onClose, onCreated, initialJob = null, initialCandidate = null, showJobDiva = false }) {
  const candidate = initialCandidate?.raw || {};
  const job = initialJob?.raw || {};
  const [form, setForm] = useState({
    ...initialForm,
    candidateFirstName: candidate.firstName || '',
    candidateLastName: candidate.lastName || '',
    candidateEmail: candidate.email || '',
    clientName: job.company || ''
  });
  const [resume, setResume] = useState(null);
  const [bgc, setBgc] = useState(null);
  const [legalId, setLegalId] = useState(null);
  const [jobDescription, setJobDescription] = useState(null);
  const [selectedJob, setSelectedJob] = useState(initialJob);
  const [selectedCandidate, setSelectedCandidate] = useState(initialCandidate);
  const [candidateLookupDisabled, setCandidateLookupDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const handleFile = (setter) => (event) => {
    const file = event.target.files?.[0] || null;
    if (file && file.size > MAX_FILE_BYTES) {
      setError(`${file.name} exceeds the 15 MB limit.`);
      event.target.value = '';
      return;
    }
    setError('');
    setter(file);
  };

  const fetchJobs = useCallback(async (query) => {
    const { data } = await ncfAPI.searchJobs(query);
    return (data.jobs || []).map(job => ({
      id: String(job.id),
      label: job.title || `Job ${job.id}`,
      sublabel: [job.company, job.location, job.reference].filter(Boolean).join(' • '),
      raw: job
    }));
  }, []);

  const fetchCandidates = useCallback(async (query) => {
    try {
      const { data } = await ncfAPI.searchCandidates(query);
      return (data.candidates || []).map(candidate => ({
        id: String(candidate.id),
        label: candidate.name || `Candidate ${candidate.id}`,
        sublabel: [candidate.title, candidate.email, candidate.location].filter(Boolean).join(' • '),
        raw: candidate
      }));
    } catch (err) {
      // The candidate search is optional; hide the control rather than blocking the form.
      if (err?.response?.data?.code === 'JOBDIVA_CANDIDATES_NOT_CONFIGURED') {
        setCandidateLookupDisabled(true);
        return [];
      }
      throw err;
    }
  }, []);

  const handleCandidateSelect = async (option) => {
    setSelectedCandidate(option);
    if (!option) return;

    // Prefill blank fields only — never clobber what the user already typed.
    try {
      const { data } = await ncfAPI.getCandidate(option.id);
      const candidate = data.candidate || {};
      setSelectedCandidate(previous => ({
        ...previous,
        raw: candidate
      }));
      setForm(prev => ({
        ...prev,
        candidateFirstName: prev.candidateFirstName || candidate.firstName || '',
        candidateLastName: prev.candidateLastName || candidate.lastName || '',
        candidateEmail: prev.candidateEmail || candidate.email || ''
      }));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load the selected candidate details.');
    }
  };

  const handleJobSelect = (option) => {
    setSelectedJob(option);
    if (option?.raw?.company) {
      setForm(prev => ({ ...prev, clientName: prev.clientName || option.raw.company }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!resume) {
      setError('A resume file is required.');
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value !== '' && value !== null) formData.append(key, value);
    });
    formData.append('resume', resume);
    if (bgc) formData.append('bgc', bgc);
    if (legalId) formData.append('legalId', legalId);
    if (jobDescription) formData.append('jobDescription', jobDescription);
    if (selectedJob?.raw) formData.append('selectedJob', JSON.stringify(selectedJob.raw));
    if (selectedCandidate?.raw) formData.append('selectedCandidate', JSON.stringify(selectedCandidate.raw));

    setSubmitting(true);
    try {
      const { data } = await ncfAPI.create(formData);
      onCreated(data.ncf);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create the NCF.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ncf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Consultant Form</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="ncf-form">
          {error && <div className="alert alert-error">{error}</div>}

          {showJobDiva && <fieldset className="ncf-fieldset">
            <legend>JobDiva prefill (optional)</legend>

            <div className="form-group">
              <label>Job</label>
              <Typeahead
                value={selectedJob}
                onSelect={handleJobSelect}
                fetchOptions={fetchJobs}
                minChars={2}
                placeholder="Search open jobs by title, ID or client..."
              />
            </div>

            {!candidateLookupDisabled && (
              <div className="form-group">
                <label>Candidate</label>
                <Typeahead
                  value={selectedCandidate}
                  onSelect={handleCandidateSelect}
                  fetchOptions={fetchCandidates}
                  minChars={3}
                  placeholder="Search JobDiva candidates by name or email..."
                />
              </div>
            )}
          </fieldset>}

          <fieldset className="ncf-fieldset">
            <legend>Candidate details</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ncf-first">First name</label>
                <input
                  id="ncf-first"
                  type="text"
                  value={form.candidateFirstName}
                  onChange={(e) => setField('candidateFirstName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="ncf-last">Last name</label>
                <input
                  id="ncf-last"
                  type="text"
                  value={form.candidateLastName}
                  onChange={(e) => setField('candidateLastName', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ncf-email">Candidate email *</label>
              <input
                id="ncf-email"
                type="email"
                required
                value={form.candidateEmail}
                onChange={(e) => setField('candidateEmail', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ncf-rate">Desired pay rate *</label>
                <input
                  id="ncf-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={form.payRate}
                  onChange={(e) => setField('payRate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="ncf-rate-type">Rate type</label>
                <select
                  id="ncf-rate-type"
                  value={form.payRateType}
                  onChange={(e) => setField('payRateType', e.target.value)}
                >
                  <option value="hourly">Hourly</option>
                  <option value="salary">Salary</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="ncf-start">Desired start date *</label>
                <input
                  id="ncf-start"
                  type="date"
                  required
                  value={form.desiredStartDate}
                  onChange={(e) => setField('desiredStartDate', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ncf-client">Client</label>
              <input
                id="ncf-client"
                type="text"
                value={form.clientName}
                onChange={(e) => setField('clientName', e.target.value)}
              />
            </div>
          </fieldset>

          <fieldset className="ncf-fieldset">
            <legend>Documents</legend>

            <div className="form-group">
              <label htmlFor="ncf-resume">Resume * (PDF or DOCX)</label>
              <input id="ncf-resume" type="file" accept={ACCEPT} onChange={handleFile(setResume)} required />
            </div>
            <div className="form-group">
              <label htmlFor="ncf-bgc">BGC form (PDF or DOCX)</label>
              <input id="ncf-bgc" type="file" accept={ACCEPT} onChange={handleFile(setBgc)} />
            </div>
            <div className="form-group">
              <label htmlFor="ncf-id">Legal ID (PDF or image)</label>
              <input id="ncf-id" type="file" accept={ACCEPT} onChange={handleFile(setLegalId)} />
            </div>
            <div className="form-group">
              <label htmlFor="ncf-job-description">Job description (PDF or DOCX)</label>
              <input id="ncf-job-description" type="file" accept={ACCEPT} onChange={handleFile(setJobDescription)} />
            </div>
          </fieldset>

          <div className="form-group">
            <label htmlFor="ncf-notes">Notes</label>
            <textarea
              id="ncf-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Processing…' : 'Create NCF'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NcfUploadModal;
